// Shared rating vocabulary for the Swim Coach conversation views.
//
// Conversations are scored by an LLM evaluator in the backend
// (cloud/swim-coach/logic/swim-coach-evaluator.ts) on two independent axes,
// stored as SwimCoachEvaluation rows with a rollup denormalized onto the
// conversation. Both the list and the detail view render those numbers, so the
// thresholds, labels and colours live here rather than being written twice and
// drifting apart.

export const EVALUATION_CLASS = 'SwimCoachEvaluation'

// Rollup fields on SwimCoachConversation, written by the backend after each
// evaluation. Mirrors SwimCoachConversationDao.FIELD.
export const ROLLUP_FIELD = {
  EVALUATED_TURNS: 'evaluatedTurns',
  AVG_COACH_SCORE: 'avgCoachScore',
  AVG_CX_SCORE: 'avgCxScore',
  GATED_TURNS: 'gatedTurns',
  LAST_EVALUATED_AT: 'lastEvaluatedAt'
}

// Fields on a SwimCoachEvaluation row. Mirrors SwimCoachEvaluationDao.FIELD.
export const EVALUATION_FIELD = {
  MESSAGE: 'message',
  TURN_INDEX: 'turnIndex',
  COACH_SCORE: 'coachScore',
  CX_SCORE: 'cxScore',
  DIMENSIONS_SCORED: 'dimensionsScored',
  GATED: 'gated',
  GATE_TYPES: 'gateTypes',
  GATES: 'gates',
  CX: 'cx',
  CX_RATIONALE: 'cxRationale',
  CX_EVIDENCE: 'cxEvidence',
  USER_REACTION: 'userReaction',
  PRIMARY_INTENT: 'primaryIntent',
  EVALUATOR_VERSION: 'evaluatorVersion'
}

// Boundaries on the 0-1 scores. "Good" and "weak" are review triage bands, not
// anything the evaluator itself asserts — it reports 0-3 per dimension and the
// backend does the arithmetic.
export const GOOD_SCORE = 0.8
export const WEAK_SCORE = 0.6

// Max raw score for a single evaluator dimension. Mirrors
// COACH_EVAL_MAX_DIMENSION_SCORE in the backend.
export const MAX_DIMENSION_SCORE = 3

// The four weighted coach dimensions, in the backend's declared order with their
// weights. Shown so a reader can see why a coach score landed where it did
// rather than having to know the rubric.
export const COACH_DIMENSIONS = [
  { key: 'coachingCorrectness', label: 'Coaching correctness', weight: 0.35 },
  { key: 'personalization', label: 'Personalization', weight: 0.3 },
  { key: 'efficiency', label: 'Efficiency', weight: 0.2 },
  { key: 'voice', label: 'Voice', weight: 0.15 }
]

export const SCORE_TITLE = {
  COACH: 'Coach score: the coaching craft in the response ' +
    '(correctness, personalization, efficiency, voice). Any hard-failure gate ' +
    'forces it to 0.',
  CX: 'CX score: how well the response served the swimmer, judged only from ' +
    'what they did next.'
}

// Why most conversations carry no rating at all. Worth stating wherever an
// empty rating shows up — otherwise it reads as a broken pipeline.
export const SAMPLING_NOTE =
  'Evaluation is sampled per conversation, so most conversations are never ' +
  'rated. The last response in a conversation is never rated either — ' +
  'scoring needs the swimmer’s reaction to it.'

export function hasScore (value) {
  return typeof value === 'number' && isFinite(value)
}

export function formatScore (value) {
  return hasScore(value) ? value.toFixed(2) : '—'
}

// Bootstrap badge class for a 0-1 score. A gated turn is always danger: its
// score was forced to 0 by a hard failure, not averaged down.
export function scoreBadgeClass (value, gated) {
  if (gated) return 'text-bg-danger'
  if (!hasScore(value)) return 'text-bg-secondary'
  if (value >= GOOD_SCORE) return 'text-bg-success'
  if (value >= WEAK_SCORE) return 'text-bg-warning'
  return 'text-bg-danger'
}

// Reads the denormalized rollup off a conversation. Returns null when the
// conversation was never sampled, which is the common case.
export function readRollup (conversation) {
  const evaluatedTurns = conversation.get(ROLLUP_FIELD.EVALUATED_TURNS)
  if (!evaluatedTurns) return null
  return {
    evaluatedTurns,
    avgCoachScore: conversation.get(ROLLUP_FIELD.AVG_COACH_SCORE),
    avgCxScore: conversation.get(ROLLUP_FIELD.AVG_CX_SCORE),
    gatedTurns: conversation.get(ROLLUP_FIELD.GATED_TURNS) || 0,
    lastEvaluatedAt: conversation.get(ROLLUP_FIELD.LAST_EVALUATED_AT) || null
  }
}

// Builds the same rollup from evaluation rows already in hand.
//
// The detail view uses this rather than the denormalized copy so the summary can
// never disagree with the per-turn ratings listed directly beneath it. The
// rollup field exists for the list, where sorting and filtering have to happen
// server-side.
export function summarizeEvaluations (evaluations) {
  if (!evaluations || !evaluations.length) return null
  let coachSum = 0
  let cxSum = 0
  let gatedTurns = 0
  evaluations.forEach((evaluation) => {
    coachSum += evaluation.get(EVALUATION_FIELD.COACH_SCORE) || 0
    cxSum += evaluation.get(EVALUATION_FIELD.CX_SCORE) || 0
    if (evaluation.get(EVALUATION_FIELD.GATED)) gatedTurns += 1
  })
  return {
    evaluatedTurns: evaluations.length,
    avgCoachScore: coachSum / evaluations.length,
    avgCxScore: cxSum / evaluations.length,
    gatedTurns
  }
}

// Indexes evaluations by the id of the message they scored, so the transcript
// can put each rating next to the response it judged.
export function indexEvaluationsByMessage (evaluations) {
  const byMessageId = {}
  if (!evaluations) return byMessageId
  evaluations.forEach((evaluation) => {
    const message = evaluation.get(EVALUATION_FIELD.MESSAGE)
    if (message && message.id) {
      byMessageId[message.id] = evaluation
    }
  })
  return byMessageId
}
